import { useSearchParams } from 'react-router-dom';
import { MentionStatusValue } from '../../domain/entities/RedditMention';

/**
 * Mention filters interface
 * Maps to URL search parameters for filter persistence
 *
 * SESSION_037_S7: Added sortBy and sortDescending with defaults
 * - Default status is 'Pending' (business owners see unverified mentions first)
 * - Default sort is by CreatedAt descending (most recent first)
 */
export interface MentionFilters {
  /** Current page number (1-based, default: 1) */
  pageNumber: number;

  /** Items per page (default: 50) */
  pageSize: number;

  /** Filter by status (default: 'Pending') */
  status?: MentionStatusValue | 'All';

  /** Filter by subreddit (optional) */
  subreddit?: string;

  /** Filter by date from (ISO date string, optional) */
  dateFrom?: string;

  /** Filter by date to (ISO date string, optional) */
  dateTo?: string;

  /** Sort field (default: 'CreatedAt') */
  sortBy: string;

  /** Sort direction (default: true for descending/newest first) */
  sortDescending: boolean;
}

/**
 * Hook to manage mention filters via URL search parameters
 * Provides filter persistence across page reloads and shareable URLs
 *
 * Session 004: Created for mention management list
 *
 * @returns Tuple of [filters, setFilters]
 */
export function useMentionFilters(): [MentionFilters, (filters: Partial<MentionFilters>) => void] {
  const [searchParams, setSearchParams] = useSearchParams();

  // Parse current filters from URL
  // SESSION_037_S7: Default to 'Pending' status and sort by CreatedAt descending
  const filters: MentionFilters = {
    pageNumber: parseInt(searchParams.get('page') ?? '1', 10),
    pageSize: parseInt(searchParams.get('pageSize') ?? '50', 10),
    status: (searchParams.get('status') as MentionStatusValue | 'All' | null) ?? 'Pending',
    subreddit: searchParams.get('subreddit') ?? undefined,
    dateFrom: searchParams.get('dateFrom') ?? undefined,
    dateTo: searchParams.get('dateTo') ?? undefined,
    sortBy: searchParams.get('sortBy') ?? 'CreatedAt',
    sortDescending: searchParams.get('sortDescending') !== 'false' // Default true
  };

  // Update filters in URL
  const setFilters = (newFilters: Partial<MentionFilters>): void => {
    const params = new URLSearchParams(searchParams);

    // Update or remove each filter parameter
    Object.entries(newFilters).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') {
        params.delete(key);
      } else {
        // Map field names for URL (pageNumber → page)
        const paramKey = key === 'pageNumber' ? 'page' : key;
        params.set(paramKey, String(value));
      }
    });

    setSearchParams(params);
  };

  return [filters, setFilters];
}
